/**
 * Date/time helpers — all in PKT (Asia/Karachi, UTC+05:00).
 *
 * Server stores timestamps as ISO 8601 (UTC). The helpers below format for
 * display. Never trust the browser's `new Date()` timezone — always go
 * through these helpers.
 */

const PKT_TZ = 'Asia/Karachi';
const DAY_MS = 86_400_000;
const RENEWAL_CALENDAR = new Intl.DateTimeFormat('en-GB', {
  timeZone: PKT_TZ, year: 'numeric', month: '2-digit', day: '2-digit',
});
const PRINTED_DATE = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'UTC', year: 'numeric', month: '2-digit', day: '2-digit',
});

function numericDate(formatter: Intl.DateTimeFormat, value: Date): string {
  return formatter.formatToParts(value).map((part) => part.type === 'year' ? part.value.padStart(4, '0') : part.value).join('');
}

export function renewalBusinessDate(now = new Date()): string {
  const parts = RENEWAL_CALENDAR.formatToParts(now);
  const part = (type: string) => parts.find((p) => p.type === type)!.value;
  return `${part('year')}-${part('month')}-${part('day')}`;
}

export function documentRenewalCutoff(now = new Date()): Date {
  return new Date(Date.parse(`${renewalBusinessDate(now)}T00:00:00Z`) + 61 * DAY_MS);
}

export function documentRenewalDays(expiry: string | Date, now = new Date()): number {
  const printedDate = new Date(expiry).toISOString().slice(0, 10);
  return (Date.parse(`${printedDate}T00:00:00Z`) - Date.parse(`${renewalBusinessDate(now)}T00:00:00Z`)) / DAY_MS;
}

export function documentRenewalLabel(expiry: string | Date, now = new Date()): string {
  const days = documentRenewalDays(expiry, now);
  if (days < 0) return `Expired — ${-days} ${days === -1 ? 'day' : 'days'} overdue`;
  if (days === 0) return 'Expires today';
  return `Expires in ${days} ${days === 1 ? 'day' : 'days'}`;
}

export function formatDocumentExpiry(expiry: string | Date): string {
  return numericDate(PRINTED_DATE, new Date(expiry));
}

export function millisecondsUntilRenewalDay(now = new Date()): number {
  const nextMidnight = Date.parse(`${renewalBusinessDate(now)}T00:00:00Z`) + DAY_MS - 5 * 3_600_000;
  return nextMidnight - now.getTime();
}

const FORMATTER_DATE = new Intl.DateTimeFormat('en-GB', {
  timeZone: PKT_TZ, day: '2-digit', month: '2-digit', year: 'numeric',
});
const FORMATTER_DATETIME = new Intl.DateTimeFormat('en-GB', {
  timeZone: PKT_TZ, day: '2-digit', month: '2-digit', year: 'numeric',
  hour: '2-digit', minute: '2-digit', hour12: false,
});
const FORMATTER_TIME = new Intl.DateTimeFormat('en-GB', {
  timeZone: PKT_TZ, hour: '2-digit', minute: '2-digit', hour12: false,
});

/** Format an ISO timestamp as `01/08/2026` in PKT. */
export function formatDate(iso: string): string {
  return numericDate(FORMATTER_DATE, new Date(iso));
}

/** Format an ISO timestamp as `01/08/2026, 19:30` in PKT. */
export function formatDateTime(iso: string): string {
  return numericDate(FORMATTER_DATETIME, new Date(iso));
}

/** Format an ISO timestamp as `19:30` in PKT. */
export function formatTime(iso: string): string {
  return FORMATTER_TIME.format(new Date(iso));
}

/** Today at 00:00 PKT, as ISO. */
export function todayStartISO(): string {
  const now = new Date();
  const pkt = new Date(now.toLocaleString('en-US', { timeZone: PKT_TZ }));
  pkt.setHours(0, 0, 0, 0);
  return pkt.toISOString();
}

/** Days from now (negative = past, positive = future). */
export function daysFromNow(iso: string): number {
  const target = new Date(iso).getTime();
  const now = Date.now();
  return Math.round((target - now) / (1000 * 60 * 60 * 24));
}

/** Bucket a date for the period filter (per STATUSES.PERIOD_BUCKETS). */
export type PeriodBucket =
  | 'Overdue'
  | 'Due Today'
  | 'Due This Week'
  | 'Due This Month'
  | 'Due After 30 Days'
  | 'Completed';

export function bucketForDate(iso: string, completed: boolean): PeriodBucket {
  if (completed) return 'Completed';
  const days = daysFromNow(iso);
  if (days < 0) return 'Overdue';
  if (days === 0) return 'Due Today';
  if (days <= 7) return 'Due This Week';
  if (days <= 31) return 'Due This Month';
  return 'Due After 30 Days';
}